import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle,
  Clock,
  History,
  KeyRound,
  Lock,
  LogIn,
  Monitor,
  Shield,
  ShieldCheck,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import { motion } from 'framer-motion';

import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { PasswordStrengthIndicator } from '../../components/security/PasswordStrengthIndicator';
import { useThemeStore } from '../../stores/theme.store';
import { useAuthStore } from '../../stores/auth.store';
import {
  KnownDevice,
  useChangePassword,
  useCreatePin,
  useKnownDevices,
  useLoginActivities,
  usePinStatus,
  useRemoveDevice,
  useTransactionLogs,
  useUpdatePin,
} from '../../hooks/useUserSecurity';

// ============================================================
// Utils
// ============================================================

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatCurrency(amount: string | number): string {
  const cents = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(cents)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

function translateOperationType(type: string): string {
  const map: Record<string, string> = {
    PIX_SEND: 'PIX enviado',
    PIX_RECEIVE: 'PIX recebido',
    BOLETO_PAY: 'Boleto pago',
    TRANSFER: 'Transferência',
    CARD_PAYMENT: 'Pagamento cartão',
  };
  return map[type] ?? type;
}

function translateTransactionStatus(status: string): { label: string; color: string } {
  switch (status) {
    case 'INITIATED':
      return { label: 'Iniciada', color: 'bg-surface-700 text-surface-200' };
    case 'VALIDATED':
      return { label: 'Validada', color: 'bg-blue-500/20 text-blue-400' };
    case 'EXECUTED':
      return { label: 'Executada', color: 'bg-green-500/20 text-green-400' };
    case 'FAILED':
      return { label: 'Falhou', color: 'bg-red-500/20 text-red-400' };
    case 'BLOCKED':
      return { label: 'Bloqueada', color: 'bg-yellow-500/20 text-yellow-400' };
    default:
      return { label: status, color: 'bg-surface-700 text-surface-200' };
  }
}

// ============================================================
// Section card primitive
// ============================================================

interface SectionCardProps {
  title: string;
  description?: string;
  icon: React.ElementType;
  action?: React.ReactNode;
  children: React.ReactNode;
}

function SectionCard({
  title,
  description,
  icon: Icon,
  action,
  children,
}: SectionCardProps) {
  const { isDarkMode } = useThemeStore();
  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const descClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400 shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className={`text-lg font-semibold ${titleClass}`}>{title}</h2>
            {description && (
              <p className={`text-sm mt-0.5 ${descClass}`}>{description}</p>
            )}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </Card>
  );
}

// ============================================================
// Toast helper
// ============================================================

type ToastState = { type: 'success' | 'error'; message: string } | null;

function useToast() {
  const [toast, setToast] = useState<ToastState>(null);
  const show = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };
  return { toast, show };
}

function ToastViewport({ toast }: { toast: ToastState }) {
  if (!toast) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
        toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      {toast.type === 'success' ? (
        <CheckCircle className="w-5 h-5" />
      ) : (
        <AlertCircle className="w-5 h-5" />
      )}
      <span className="text-sm font-medium">{toast.message}</span>
    </motion.div>
  );
}

// ============================================================
// PIN modal
// ============================================================

interface PinFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'update';
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function PinFormModal({
  isOpen,
  mode,
  onClose,
  onSuccess,
  onError,
}: PinFormModalProps) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createPin = useCreatePin();
  const updatePin = useUpdatePin();
  const isLoading = createPin.isPending || updatePin.isPending;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      setError(null);
    }
  }, [isOpen]);

  const validate = (): string | null => {
    if (mode === 'update' && !/^\d{6}$/.test(currentPin)) {
      return 'PIN atual deve ter 6 dígitos.';
    }
    if (!/^\d{6}$/.test(newPin)) return 'Novo PIN deve ter 6 dígitos.';
    if (newPin !== confirmPin) return 'Os PINs não conferem.';
    if (/^(\d)\1{5}$/.test(newPin)) {
      return 'Evite PIN com todos os dígitos iguais.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      if (mode === 'create') {
        await createPin.mutateAsync({ pin: newPin });
        onSuccess('PIN cadastrado com sucesso.');
      } else {
        await updatePin.mutateAsync({ currentPin, newPin });
        onSuccess('PIN alterado com sucesso.');
      }
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : 'Erro ao salvar PIN.');
      setError(message);
      onError(message);
    }
  };

  const title = mode === 'create' ? 'Cadastrar PIN de Transação' : 'Alterar PIN';
  const submitLabel = mode === 'create' ? 'Cadastrar PIN' : 'Alterar PIN';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        {mode === 'update' && (
          <Input
            label="PIN atual"
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={currentPin}
            onChange={(e) =>
              setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder="••••••"
          />
        )}
        <Input
          label="Novo PIN (6 dígitos)"
          type="password"
          inputMode="numeric"
          maxLength={6}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="••••••"
        />
        <Input
          label="Confirmar novo PIN"
          type="password"
          inputMode="numeric"
          maxLength={6}
          value={confirmPin}
          onChange={(e) =>
            setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))
          }
          placeholder="••••••"
        />

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isLoading}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Change password modal
// ============================================================

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function ChangePasswordModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLogoutNotice, setShowLogoutNotice] = useState(false);
  const { logout } = useAuthStore();

  const changePassword = useChangePassword();

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setShowLogoutNotice(false);
    }
  }, [isOpen]);

  const canSubmit =
    !!currentPassword && !!newPassword && isPasswordValid && newPassword === confirmPassword;

  const handleSubmit = async () => {
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    if (!isPasswordValid) {
      setError('A nova senha não atende aos requisitos.');
      return;
    }
    setError(null);
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      onSuccess('Senha alterada com sucesso. Você precisará fazer login novamente.');
      setShowLogoutNotice(true);
      // Auto-logout after a short delay so the user sees the message
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2500);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : 'Erro ao alterar senha.');
      setError(message);
      onError(message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Alterar senha" size="md">
      <div className="space-y-4">
        {showLogoutNotice ? (
          <div className="flex items-start gap-2 p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Senha alterada.</p>
              <p className="text-xs mt-1 opacity-90">
                Por segurança, todas as sessões foram encerradas. Redirecionando para login…
              </p>
            </div>
          </div>
        ) : (
          <>
            <Input
              label="Senha atual"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Input
              label="Nova senha"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
            <PasswordStrengthIndicator
              password={newPassword}
              onValidChange={setIsPasswordValid}
            />
            <Input
              label="Confirmar nova senha"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              error={
                confirmPassword && newPassword !== confirmPassword
                  ? 'As senhas não conferem'
                  : undefined
              }
            />

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={changePassword.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
                isLoading={changePassword.isPending}
              >
                Alterar senha
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Devices card
// ============================================================

function DevicesCard({
  onSuccess,
  onError,
}: {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const { data: devices, isLoading } = useKnownDevices();
  const removeDevice = useRemoveDevice();
  const [confirmDevice, setConfirmDevice] = useState<KnownDevice | null>(null);
  const { isDarkMode } = useThemeStore();
  const mutedClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const strongClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';

  const handleRemove = async () => {
    if (!confirmDevice) return;
    try {
      await removeDevice.mutateAsync(confirmDevice.id);
      onSuccess('Dispositivo removido.');
      setConfirmDevice(null);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : 'Erro ao remover dispositivo.');
      onError(message);
    }
  };

  return (
    <SectionCard
      title="Dispositivos conhecidos"
      description="Dispositivos marcados como confiáveis dispensam confirmação por email em operações sensíveis."
      icon={Smartphone}
    >
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : !devices || devices.length === 0 ? (
        <div className={`text-center py-8 ${mutedClass}`}>
          <Monitor className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <p className="text-sm">Nenhum dispositivo registrado.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => (
            <div
              key={device.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50"
            >
              <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center shrink-0">
                <Monitor className="w-5 h-5 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-medium ${strongClass} truncate`}>
                    {device.deviceName ?? 'Dispositivo desconhecido'}
                  </p>
                  {device.trusted ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Confiável
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                      Não confirmado
                    </span>
                  )}
                </div>
                <div className={`text-xs mt-0.5 ${mutedClass}`}>
                  {device.ipAddress} · Primeiro acesso {formatDateTime(device.firstSeenAt)} · Último acesso {formatDateTime(device.lastSeenAt)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDevice(device)}
                title="Remover dispositivo"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={!!confirmDevice}
        onClose={() => setConfirmDevice(null)}
        title="Remover dispositivo"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Esse dispositivo precisará ser reconfirmado no próximo acesso. Continuar?
            </p>
          </div>
          {confirmDevice && (
            <div className="p-3 rounded-lg bg-surface-800 text-sm">
              <p className="font-medium">{confirmDevice.deviceName ?? 'Dispositivo desconhecido'}</p>
              <p className="text-xs opacity-75 mt-0.5">
                {confirmDevice.ipAddress} · {formatDateTime(confirmDevice.lastSeenAt)}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDevice(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleRemove}
              isLoading={removeDevice.isPending}
            >
              Remover
            </Button>
          </div>
        </div>
      </Modal>
    </SectionCard>
  );
}

// ============================================================
// Activity card (logins + transactions tabs + pagination)
// ============================================================

const PAGE_LIMIT = 10;

function ActivityCard() {
  const [tab, setTab] = useState<'logins' | 'transactions'>('logins');
  const [loginsPage, setLoginsPage] = useState(1);
  const [txPage, setTxPage] = useState(1);

  const loginsQuery = useLoginActivities(loginsPage, PAGE_LIMIT);
  const txQuery = useTransactionLogs(txPage, PAGE_LIMIT);

  const { isDarkMode } = useThemeStore();
  const mutedClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';

  const totalLoginsPages = Math.max(
    1,
    Math.ceil((loginsQuery.data?.total ?? 0) / PAGE_LIMIT),
  );
  const totalTxPages = Math.max(
    1,
    Math.ceil((txQuery.data?.total ?? 0) / PAGE_LIMIT),
  );

  return (
    <SectionCard
      title="Atividade de login e transações"
      description="Histórico de acessos e operações da sua conta."
      icon={History}
    >
      <div className="flex items-center gap-2 border-b border-surface-800 mb-4">
        <button
          type="button"
          onClick={() => setTab('logins')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'logins'
              ? 'border-primary-500 text-primary-400'
              : `border-transparent ${mutedClass} hover:text-surface-200`
          }`}
        >
          <LogIn className="w-4 h-4 inline mr-1.5" />
          Logins
        </button>
        <button
          type="button"
          onClick={() => setTab('transactions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'transactions'
              ? 'border-primary-500 text-primary-400'
              : `border-transparent ${mutedClass} hover:text-surface-200`
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1.5" />
          Transações
        </button>
      </div>

      {tab === 'logins' ? (
        <>
          {loginsQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !loginsQuery.data?.data.length ? (
            <div className={`text-center py-8 ${mutedClass}`}>
              <LogIn className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">Nenhuma atividade registrada.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {loginsQuery.data.data.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50"
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      activity.status === 'SUCCESS'
                        ? 'bg-green-500/20 text-green-400'
                        : activity.status === 'FAILED'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                    }`}
                  >
                    {activity.status === 'SUCCESS' ? (
                      <Check className="w-4 h-4" />
                    ) : activity.status === 'FAILED' ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-sm font-medium ${
                          activity.status === 'SUCCESS'
                            ? 'text-green-400'
                            : activity.status === 'FAILED'
                              ? 'text-red-400'
                              : 'text-yellow-400'
                        }`}
                      >
                        {activity.status === 'SUCCESS'
                          ? 'Login bem-sucedido'
                          : activity.status === 'FAILED'
                            ? 'Tentativa falhou'
                            : 'Login bloqueado'}
                      </span>
                    </div>
                    <div className={`text-xs mt-0.5 ${mutedClass}`}>
                      {activity.deviceName ?? 'Dispositivo desconhecido'} ·{' '}
                      {activity.ipAddress}
                      {activity.location ? ` · ${activity.location}` : ''}
                    </div>
                  </div>
                  <div className={`text-xs ${mutedClass} shrink-0`}>
                    {formatDateTime(activity.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pagination
            page={loginsPage}
            totalPages={totalLoginsPages}
            onChange={setLoginsPage}
          />
        </>
      ) : (
        <>
          {txQuery.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !txQuery.data?.data.length ? (
            <div className={`text-center py-8 ${mutedClass}`}>
              <Clock className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">Nenhuma transação registrada.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {txQuery.data.data.map((log) => {
                const status = translateTransactionStatus(log.status);
                return (
                  <div
                    key={log.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 flex-wrap"
                  >
                    <div className="w-9 h-9 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-surface-100">
                          {translateOperationType(log.operationType)}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${status.color}`}
                        >
                          {status.label}
                        </span>
                      </div>
                      <div className={`text-xs mt-0.5 ${mutedClass}`}>
                        {formatCurrency(log.amount)} · IP {log.ipAddress}
                      </div>
                    </div>
                    <div className={`text-xs ${mutedClass} shrink-0`}>
                      {formatDateTime(log.createdAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pagination
            page={txPage}
            totalPages={totalTxPages}
            onChange={setTxPage}
          />
        </>
      )}
    </SectionCard>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const { isDarkMode } = useThemeStore();
  const mutedClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-2 pt-4 mt-4 border-t border-surface-800">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
      >
        Anterior
      </Button>
      <span className={`text-xs ${mutedClass}`}>
        Página {page} de {totalPages}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
      >
        Próxima
      </Button>
    </div>
  );
}

// ============================================================
// Main page
// ============================================================

export function SecuritySettings() {
  const { user } = useAuthStore();
  const { isDarkMode } = useThemeStore();
  const { toast, show } = useToast();

  const [showPinModal, setShowPinModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const pinStatusQuery = usePinStatus();

  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const subtitleClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';

  const pinMode: 'create' | 'update' = pinStatusQuery.data?.hasPin
    ? 'update'
    : 'create';

  const pinCardSubtitle = useMemo(() => {
    if (pinStatusQuery.isLoading) return 'Carregando…';
    return pinStatusQuery.data?.hasPin
      ? 'PIN cadastrado. Você pode alterá-lo a qualquer momento.'
      : 'PIN não cadastrado. Cadastre para autorizar operações sensíveis.';
  }, [pinStatusQuery.data, pinStatusQuery.isLoading]);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <ToastViewport toast={toast} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1
            className={`text-2xl font-bold ${titleClass} flex items-center gap-3`}
          >
            <Shield className="w-7 h-7 text-primary-400" />
            Segurança
          </h1>
          <p className={`${subtitleClass} mt-1`}>
            {user ? `Olá, ${user.name}. ` : ''}Gerencie o PIN, senha, dispositivos
            e histórico de atividade da sua conta.
          </p>
        </div>
      </div>

      {/* Card 1: PIN */}
      <SectionCard
        title="PIN de Transação"
        description={pinCardSubtitle}
        icon={KeyRound}
        action={
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowPinModal(true)}
            disabled={pinStatusQuery.isLoading}
          >
            {pinStatusQuery.data?.hasPin ? 'Alterar PIN' : 'Cadastrar PIN'}
          </Button>
        }
      >
        <div
          className={`flex items-center gap-3 p-4 rounded-lg ${
            pinStatusQuery.data?.hasPin
              ? 'bg-green-500/10 border border-green-500/20'
              : 'bg-yellow-500/10 border border-yellow-500/20'
          }`}
        >
          {pinStatusQuery.data?.hasPin ? (
            <>
              <ShieldCheck className="w-6 h-6 text-green-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-400">
                  PIN cadastrado
                </p>
                <p className="text-xs text-green-300/80 mt-0.5">
                  Seu PIN protege operações acima do limite configurado.
                </p>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-400">
                  PIN não cadastrado
                </p>
                <p className="text-xs text-yellow-300/80 mt-0.5">
                  Você pode ser solicitado a informar o PIN em operações sensíveis.
                </p>
              </div>
            </>
          )}
        </div>
      </SectionCard>

      {/* Card 2: Password */}
      <SectionCard
        title="Senha de acesso"
        description="Mantenha sua senha forte e atualizada. Ao alterar, suas sessões serão encerradas."
        icon={Lock}
        action={
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowPasswordModal(true)}
          >
            Alterar senha
          </Button>
        }
      >
        <div className="flex items-start gap-3 p-4 rounded-lg bg-surface-800/40">
          <Lock className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
          <p className={`text-sm ${subtitleClass}`}>
            Ao alterar a senha, por segurança, todas as sessões ativas serão
            encerradas e você precisará fazer login novamente.
          </p>
        </div>
      </SectionCard>

      {/* Card 3: Devices */}
      <DevicesCard
        onSuccess={(msg) => show('success', msg)}
        onError={(msg) => show('error', msg)}
      />

      {/* Card 4: Activity */}
      <ActivityCard />

      {/* Modals */}
      <PinFormModal
        isOpen={showPinModal}
        mode={pinMode}
        onClose={() => setShowPinModal(false)}
        onSuccess={(msg) => show('success', msg)}
        onError={(msg) => show('error', msg)}
      />

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={(msg) => show('success', msg)}
        onError={(msg) => show('error', msg)}
      />
    </div>
  );
}

export default SecuritySettings;
